import { Stack, Typography } from '@hiiretail/react-foundation-ui';
import { Box, Button, Link, Switch, TextField } from '@hiiretail/synergy-ui';
import { Controller, UseFormReturn } from 'react-hook-form';

type ExternalIdpLogoutConfigFormProps = {
  control: UseFormReturn['control'];
  formState: UseFormReturn['formState'];
  isOpen: boolean;
  handleAddForm: () => void;
  disableButton?: boolean;
};

export const ExternalIdpLogoutConfigForm: React.FC<ExternalIdpLogoutConfigFormProps> = ({
  control,
  formState,
  handleAddForm,
  isOpen,
  disableButton,
}) => {
  return (
    <Stack
      sx={{
        padding: 2,
        border: '1px solid rgba(211, 211, 211, 0.48)',
        borderRadius: '4px',
      }}
      spacing={2}
      data-testid="idp-logout-config-form-wrapper"
    >
      <Box>
        <Typography fontWeight="bold" variant="body1">
          IDP Logout config
          <Typography variant="caption" fontWeight="regular" color="gray" display="block">
            External IdP logout configuration. Used by the UI to specify how to logout from the
            external IdP. For more information visit{' '}
            <Link
              to="https://developer.hiiretail.com/docs/identity-and-access-management/public/concepts/identity-providers/#idp-logout"
              external
            >
              Documentation
            </Link>
          </Typography>
        </Typography>
      </Box>

      {isOpen ? (
        <Box style={{ marginTop: '1rem' }} data-testid="idp-logout-config-form">
          <Box style={{ marginBottom: '8px' }}>
            <Controller
              control={control}
              defaultValue={false}
              name="externalIdpLogoutConfig.enabled"
              render={({ field: { value, onChange } }) => (
                <Box
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body2" fontWeight="bold">
                    Enable IDP Logout
                    <Typography variant="caption" color="text.secondary" display="block">
                      If enabled, UI will redirect to redirectUrl on logout
                    </Typography>
                  </Typography>
                  <Switch
                    onChange={onChange}
                    name="externalIdpLogoutConfig"
                    checked={value ?? false}
                  />
                </Box>
              )}
            />
          </Box>
          <Box style={{ marginBottom: '8px' }}>
            <Controller
              control={control}
              name="externalIdpLogoutConfig.redirectUrl"
              render={({ field: { ref, value, onChange } }) => (
                <TextField
                  tabIndex={0}
                  fullWidth
                  label="Redirect URL"
                  name="externalIdpLogoutConfig.redirectUrl"
                  required
                  // @ts-ignore
                  hint={
                    <span>
                      Upon logout, Hii Retail will redirect to Logout URL so identity provider can
                      perform any needed actions
                    </span>
                  }
                  type="text"
                  ref={ref}
                  onChange={onChange}
                  value={value ?? ''}
                  error={
                    formState.errors?.externalIdpLogoutConfig?.redirectUrl?.message as
                      | string
                      | undefined
                  }
                />
              )}
            />
          </Box>
        </Box>
      ) : null}

      <Box style={{ marginTop: '8px' }}>
        <Button variant="secondary" onClick={handleAddForm} disabled={disableButton}>
          {isOpen ? 'Remove IDP logout config' : 'Add IDP logout config'}
        </Button>
      </Box>
    </Stack>
  );
};
